import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { TableFilter, Filter } from '../components/TableFilter';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { ColumnToggle, Column } from '../components/ColumnToggle';
import { ExportButton } from '../components/ExportButton';
import { BulkTagModal, BulkTagModalTag } from '../components/BulkTagModal';
import { MoreVertical, Mail, Phone, MapPin, AlertCircle, CheckCircle, XCircle, Download, Tag as TagIcon, Send, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { ContactSearchInput, matchesContactSearch } from '../components/contacts/ContactSearchInput';
import { ContactInsightCell, insightColumnIds } from '../components/contacts/ContactInsightCell';
import { mockContacts } from '../data/mockContacts';
import type { ContactSearchBy } from '../types/contactInsights';
import { insightSortValue } from '../utils/contactInsights';
import { buildInsightFilters, groupContactFilters, matchesInsightFilters } from '../utils/contactFilters';
import { downloadCallList } from '../utils/callListExport';
import { SavedViewsDropdown } from '../components/SavedViewsDropdown';
import { ContactBulkActionsBar } from '../components/contacts/ContactBulkActionsBar';
import { CreateTaskModal } from '../components/CreateTaskModal';
import { useTasks } from '../contexts/TaskContext';
import { useContactViews } from '../hooks/useContactViews';
// Mock Data
export function ContactsPage() {
  const navigate = useNavigate();
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchBy, setSearchBy] = useState<ContactSearchBy>('nameEmail');
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  // Bulk Tag Modal State
  const [isBulkTagModalOpen, setIsBulkTagModalOpen] = useState(false);
  // Tag color mapping - store colors for all tags
  const [tagColors, setTagColors] = useState<Map<string, string>>(new Map([['VIP', 'purple'], ['Red Wine', 'blue'], ['White Wine', 'blue'], ['Retry', 'orange'], ['Churned', 'gray'], ['Referral', 'green']]));
  // Contact data state (so we can update it)
  const [contacts, setContacts] = useState(mockContacts);
  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  // Column state - now using Column[] format for ColumnToggle
  const [columns, setColumns] = useState<Column[]>([{
    id: 'contact',
    label: 'Contact',
    visible: true
  }, {
    id: 'phone',
    label: 'Phone',
    visible: true
  }, {
    id: 'address',
    label: 'Address',
    visible: true
  }, {
    id: 'status',
    label: 'Status',
    visible: true
  }, {
    id: 'callFlag',
    label: 'Call flag',
    visible: true
  }, {
    id: 'cadence',
    label: 'Cadence',
    visible: true
  }, {
    id: 'lastContacted',
    label: 'Last contacted',
    visible: true
  }, {
    id: 'trend',
    label: 'Trend',
    visible: true
  }, {
    id: 'skipsPauses',
    label: 'Skips & pauses (12m)',
    visible: false
  }, {
    id: 'clubOneTime',
    label: 'Club / One-time',
    visible: false
  }, {
    id: 'openTasks',
    label: 'Open tasks',
    visible: false
  }, {
    id: 'topProduct',
    label: 'Top product',
    visible: false
  }, {
    id: 'subscription',
    label: 'Subscription',
    visible: true
  }, {
    id: 'payment',
    label: 'Payment',
    visible: true
  }, {
    id: 'tags',
    label: 'Tags',
    visible: true
  }, {
    id: 'source',
    label: 'Source',
    visible: false
  }, {
    id: 'memberSince',
    label: 'Member Since',
    visible: false
  }, {
    id: 'nextBilling',
    label: 'Next Billing',
    visible: false
  }, {
    id: 'ltv',
    label: 'LTV',
    visible: false
  }, {
    id: 'totalOrders',
    label: 'Total Orders',
    visible: false
  }, {
    id: 'lastOrder',
    label: 'Last Order',
    visible: false
  }, {
    id: 'aov',
    label: 'AOV',
    visible: false
  }]);
  // Filter state
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    status: [],
    paymentStatus: [],
    subscription: [],
    source: [],
    tags: [],
    contact: [],
    phone: [],
    address: [],
    memberSince: [],
    nextBilling: [],
    ltv: [],
    totalOrders: [],
    lastOrder: [],
    aov: [],
    note: []
  });
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  // Bulk task creation
  const [isBulkTaskModalOpen, setIsBulkTaskModalOpen] = useState(false);
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);
  const {
    addTask
  } = useTasks();
  // Views: preset call lists apply their filters as chips so staff can tweak them
  const views = useContactViews({
    onApply: view => {
      setFilterValues(view ? {
        ...view.filters
      } : {});
      setAddedFilters(new Set(view ? Object.keys(view.filters) : []));
      setSortConfig(view?.sort ?? null);
      setSelectedContacts(new Set());
    }
  });
  const clearAllFilters = () => {
    setFilterValues({});
    setAddedFilters(new Set());
    setSearchQuery('');
    views.resetToAll();
  };
  useEffect(() => {
    if (!bulkMessage) return;
    const timer = setTimeout(() => setBulkMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [bulkMessage]);
  // Get visible columns in order
  const visibleColumns = useMemo(() => {
    return columns.filter(col => col.visible);
  }, [columns]);
  // Base contact filters; grouped with the call-prep filters below
  const baseFilters: Filter[] = [{
    id: 'contact',
    label: 'Contact',
    options: [],
    selectedValue: filterValues.contact?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('contact')
  }, {
    id: 'phone',
    label: 'Phone',
    options: [],
    selectedValue: filterValues.phone?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('phone')
  }, {
    id: 'address',
    label: 'Address',
    options: [],
    selectedValue: filterValues.address?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('address')
  }, {
    id: 'status',
    label: 'Status',
    options: [{
      id: '1',
      label: 'Active',
      value: 'Active'
    }, {
      id: '2',
      label: 'Paused',
      value: 'Paused'
    }, {
      id: '3',
      label: 'Cancelled',
      value: 'Cancelled'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status')
  }, {
    id: 'subscription',
    label: 'Subscription',
    options: [{
      id: '1',
      label: 'Premium Wine Club',
      value: 'Premium Wine Club'
    }, {
      id: '2',
      label: 'Standard Box',
      value: 'Standard Box'
    }, {
      id: '3',
      label: 'Starter Pack',
      value: 'Starter Pack'
    }],
    selectedValues: filterValues.subscription || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('subscription')
  }, {
    id: 'paymentStatus',
    label: 'Payment',
    options: [{
      id: '1',
      label: 'Current',
      value: 'Current'
    }, {
      id: '2',
      label: 'Failed',
      value: 'Failed'
    }],
    selectedValues: filterValues.paymentStatus || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('paymentStatus')
  }, {
    id: 'tags',
    label: 'Tags',
    options: [{
      id: '1',
      label: 'VIP',
      value: 'VIP'
    }, {
      id: '2',
      label: 'Red Wine',
      value: 'Red Wine'
    }, {
      id: '3',
      label: 'White Wine',
      value: 'White Wine'
    }, {
      id: '5',
      label: 'Retry',
      value: 'Retry'
    }, {
      id: '6',
      label: 'Churned',
      value: 'Churned'
    }, {
      id: '7',
      label: 'Referral',
      value: 'Referral'
    }],
    selectedValues: filterValues.tags || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('tags')
  }, {
    id: 'note',
    label: 'Note',
    options: [],
    selectedValue: filterValues.note?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('note')
  }, {
    id: 'source',
    label: 'Source',
    options: [{
      id: '1',
      label: 'Shopify Checkout',
      value: 'Shopify Checkout'
    }, {
      id: '2',
      label: 'Manual',
      value: 'Manual'
    }, {
      id: '3',
      label: 'POS',
      value: 'POS'
    }, {
      id: '4',
      label: 'Referral',
      value: 'Referral'
    }],
    selectedValues: filterValues.source || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('source')
  }, {
    id: 'memberSince',
    label: 'Member Since',
    options: [],
    selectedValue: filterValues.memberSince?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('memberSince')
  }, {
    id: 'nextBilling',
    label: 'Next Billing',
    options: [],
    selectedValue: filterValues.nextBilling?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('nextBilling')
  }, {
    id: 'ltv',
    label: 'LTV',
    options: [{
      id: '1',
      label: 'Under $500',
      value: 'under-500'
    }, {
      id: '2',
      label: '$500 - $1000',
      value: '500-1000'
    }, {
      id: '3',
      label: '$1000 - $1500',
      value: '1000-1500'
    }, {
      id: '4',
      label: 'Over $1500',
      value: 'over-1500'
    }],
    selectedValues: filterValues.ltv || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('ltv')
  }, {
    id: 'totalOrders',
    label: 'Total Orders',
    options: [{
      id: '1',
      label: '1-5 orders',
      value: '1-5'
    }, {
      id: '2',
      label: '6-10 orders',
      value: '6-10'
    }, {
      id: '3',
      label: '11-15 orders',
      value: '11-15'
    }, {
      id: '4',
      label: '15+ orders',
      value: '15-plus'
    }],
    selectedValues: filterValues.totalOrders || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('totalOrders')
  }, {
    id: 'lastOrder',
    label: 'Last Order',
    options: [],
    selectedValue: filterValues.lastOrder?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('lastOrder')
  }, {
    id: 'aov',
    label: 'AOV',
    options: [{
      id: '1',
      label: 'Under $75',
      value: 'under-75'
    }, {
      id: '2',
      label: '$75 - $100',
      value: '75-100'
    }, {
      id: '3',
      label: '$100 - $125',
      value: '100-125'
    }, {
      id: '4',
      label: 'Over $125',
      value: 'over-125'
    }],
    selectedValues: filterValues.aov || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('aov')
  }];
  // Club options come from the contacts themselves
  const clubNames = Array.from(new Set(contacts.map(contact => contact.subscriptionTitle))).sort();
  const availableFilters: Filter[] = groupContactFilters([...baseFilters.map(filter => filter.id === 'subscription' ? {
    ...filter,
    options: clubNames.map(name => ({
      id: name,
      label: name,
      value: name
    }))
  } : filter), ...buildInsightFilters(filterValues, openFilterDropdowns)]);
  // Filter logic
  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      if (!matchesInsightFilters(contact, filterValues)) return false;
      // Search
      if (searchQuery && !matchesContactSearch(contact, searchQuery, searchBy)) {
        return false;
      }
      // Contact Filter (search type)
      if (filterValues.contact && filterValues.contact.length > 0 && filterValues.contact[0].trim()) {
        const searchValue = filterValues.contact[0].toLowerCase();
        if (!contact.name.toLowerCase().includes(searchValue) && !contact.email.toLowerCase().includes(searchValue)) {
          return false;
        }
      }
      // Phone Filter (search type)
      if (filterValues.phone && filterValues.phone.length > 0 && filterValues.phone[0].trim()) {
        const searchValue = filterValues.phone[0].toLowerCase();
        if (!contact.phone.toLowerCase().includes(searchValue)) {
          return false;
        }
      }
      // Address Filter (search type)
      if (filterValues.address && filterValues.address.length > 0 && filterValues.address[0].trim()) {
        const searchValue = filterValues.address[0].toLowerCase();
        if (!contact.address.toLowerCase().includes(searchValue)) {
          return false;
        }
      }
      // Status Filter
      if (filterValues.status?.length > 0 && !filterValues.status.includes(contact.status)) {
        return false;
      }
      // Subscription Filter
      if (filterValues.subscription?.length > 0 && !filterValues.subscription.includes(contact.subscriptionTitle)) {
        return false;
      }
      // Payment Status Filter
      if (filterValues.paymentStatus?.length > 0 && !filterValues.paymentStatus.includes(contact.paymentStatus)) {
        return false;
      }
      // Tags Filter - contact must have at least one of the selected tags
      if (filterValues.tags?.length > 0) {
        const hasMatchingTag = filterValues.tags.some(tag => contact.tags.includes(tag));
        if (!hasMatchingTag) return false;
      }
      // Note Filter (search type)
      if (filterValues.note && filterValues.note.length > 0 && filterValues.note[0].trim()) {
        const searchValue = filterValues.note[0].toLowerCase();
        if (!contact.latestNote.toLowerCase().includes(searchValue)) {
          return false;
        }
      }
      // Source Filter
      if (filterValues.source?.length > 0 && !filterValues.source.includes(contact.source)) {
        return false;
      }
      // Member Since Filter (search type)
      if (filterValues.memberSince && filterValues.memberSince.length > 0 && filterValues.memberSince[0].trim()) {
        const searchValue = filterValues.memberSince[0].toLowerCase();
        if (!contact.memberSince.toLowerCase().includes(searchValue)) {
          return false;
        }
      }
      // Next Billing Filter (search type)
      if (filterValues.nextBilling && filterValues.nextBilling.length > 0 && filterValues.nextBilling[0].trim()) {
        const searchValue = filterValues.nextBilling[0].toLowerCase();
        if (!contact.nextBilling.toLowerCase().includes(searchValue)) {
          return false;
        }
      }
      // LTV Filter
      if (filterValues.ltv?.length > 0) {
        const ltvMatch = filterValues.ltv.some(value => {
          if (value === 'under-500') return contact.ltv < 500;
          if (value === '500-1000') return contact.ltv >= 500 && contact.ltv < 1000;
          if (value === '1000-1500') return contact.ltv >= 1000 && contact.ltv < 1500;
          if (value === 'over-1500') return contact.ltv >= 1500;
          return false;
        });
        if (!ltvMatch) return false;
      }
      // Total Orders Filter
      if (filterValues.totalOrders?.length > 0) {
        const ordersMatch = filterValues.totalOrders.some(value => {
          if (value === '1-5') return contact.totalOrders >= 1 && contact.totalOrders <= 5;
          if (value === '6-10') return contact.totalOrders >= 6 && contact.totalOrders <= 10;
          if (value === '11-15') return contact.totalOrders >= 11 && contact.totalOrders <= 15;
          if (value === '15-plus') return contact.totalOrders > 15;
          return false;
        });
        if (!ordersMatch) return false;
      }
      // Last Order Filter (search type)
      if (filterValues.lastOrder && filterValues.lastOrder.length > 0 && filterValues.lastOrder[0].trim()) {
        const searchValue = filterValues.lastOrder[0].toLowerCase();
        if (!contact.lastOrder.toLowerCase().includes(searchValue)) {
          return false;
        }
      }
      // AOV Filter
      if (filterValues.aov?.length > 0) {
        const aovMatch = filterValues.aov.some(value => {
          if (value === 'under-75') return contact.aov < 75;
          if (value === '75-100') return contact.aov >= 75 && contact.aov < 100;
          if (value === '100-125') return contact.aov >= 100 && contact.aov < 125;
          if (value === 'over-125') return contact.aov >= 125;
          return false;
        });
        if (!aovMatch) return false;
      }
      return true;
    });
  }, [searchQuery, searchBy, filterValues, contacts]);
  // Sorted and filtered contacts
  const sortedAndFilteredContacts = useMemo(() => {
    if (!sortConfig) return filteredContacts;
    const sorted = [...filteredContacts].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      switch (sortConfig.key) {
        case 'contact':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'phone':
          aValue = a.phone;
          bValue = b.phone;
          break;
        case 'address':
          aValue = a.address;
          bValue = b.address;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'subscription':
          aValue = a.subscriptionTitle;
          bValue = b.subscriptionTitle;
          break;
        case 'payment':
          aValue = a.paymentStatus;
          bValue = b.paymentStatus;
          break;
        case 'source':
          aValue = a.source;
          bValue = b.source;
          break;
        case 'memberSince':
          aValue = new Date(a.memberSince).getTime();
          bValue = new Date(b.memberSince).getTime();
          break;
        case 'nextBilling':
          aValue = a.nextBilling === '-' ? 0 : new Date(a.nextBilling).getTime();
          bValue = b.nextBilling === '-' ? 0 : new Date(b.nextBilling).getTime();
          break;
        case 'ltv':
          aValue = a.ltv;
          bValue = b.ltv;
          break;
        case 'totalOrders':
          aValue = a.totalOrders;
          bValue = b.totalOrders;
          break;
        case 'lastOrder':
          aValue = new Date(a.lastOrder).getTime();
          bValue = new Date(b.lastOrder).getTime();
          break;
        case 'aov':
          aValue = a.aov;
          bValue = b.aov;
          break;
        default:
          aValue = insightSortValue(a.insights, sortConfig.key);
          bValue = insightSortValue(b.insights, sortConfig.key);
          if (aValue === null || bValue === null) return 0;
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    return sorted;
  }, [filteredContacts, sortConfig]);
  // Handlers
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedContacts(new Set(sortedAndFilteredContacts.map(c => c.id)));
    } else {
      setSelectedContacts(new Set());
    }
  };
  const handleSelectContact = (id: string) => {
    const newSelected = new Set(selectedContacts);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedContacts(newSelected);
  };
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setFilterValues(prev => ({
      ...prev,
      [filterId]: Array.isArray(value) ? value : [value]
    }));
  };
  const handleFilterRemove = (filterId: string) => {
    const newAdded = new Set(addedFilters);
    newAdded.delete(filterId);
    setAddedFilters(newAdded);
    setFilterValues(prev => ({
      ...prev,
      [filterId]: []
    }));
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpen = new Set(openFilterDropdowns);
    if (newOpen.has(filterId)) {
      newOpen.delete(filterId);
    } else {
      newOpen.add(filterId);
    }
    setOpenFilterDropdowns(newOpen);
  };
  const handleAddFilter = (filterId: string) => {
    const newAdded = new Set(addedFilters);
    newAdded.add(filterId);
    setAddedFilters(newAdded);
  };
  // Handle sort
  const handleSort = (columnKey: string) => {
    setSortConfig(current => {
      if (!current || current.key !== columnKey) {
        return {
          key: columnKey,
          direction: 'asc'
        };
      }
      if (current.direction === 'asc') {
        return {
          key: columnKey,
          direction: 'desc'
        };
      }
      return null;
    });
  };
  // Render cell content based on column id
  const renderCell = (contact: (typeof mockContacts)[0], columnId: string) => {
    if ((insightColumnIds as readonly string[]).includes(columnId)) {
      return <ContactInsightCell insights={contact.insights} columnId={columnId} data-id="element-8225" data-mp="30qh2pbd58qec" />;
    }
    switch (columnId) {
      case 'contact':
        return <div data-id="element-8226" data-mp="2wovif4ya59u4">
            <div className="font-medium text-[#0F172A]" data-id="element-8227" data-mp="2wrhpz4l67pcc">{contact.name}</div>
            <div className="text-xs text-[#64748B]" data-id="element-8228" data-mp="3484ux7231one">{contact.email}</div>
          </div>;
      case 'phone':
        return <span className="text-sm text-[#475569] whitespace-nowrap" data-id="element-8229" data-mp="399c0czgr6bqw">
            {contact.phone}
          </span>;
      case 'address':
        return <div className="text-sm text-[#475569]" data-id="element-8230" data-mp="2z9l34q0t5in4">
            <div className="whitespace-nowrap" data-id="element-8231" data-mp="2zacyq0uengde">
              {contact.address.split(',')[0]}
            </div>
            <div className="whitespace-nowrap" data-id="element-8232" data-mp="3n1igiljwoiqc">
              {contact.address.split(',').slice(1).join(',').trim()}
            </div>
          </div>;
      case 'status':
        return <Badge variant={contact.status === 'Active' ? 'success' : contact.status === 'Paused' ? 'warning' : 'error'} data-id="element-8233" data-mp="3d92zbqgfny6i">
            {contact.status}
          </Badge>;
      case 'subscription':
        return <span className="text-sm text-[#0F172A] font-medium" data-id="element-8234" data-mp="9rcezs8g9nvf">
            {contact.subscriptionTitle}
          </span>;
      case 'payment':
        return <Badge variant={contact.paymentStatus === 'Current' ? 'success' : 'error'} data-id="element-8235" data-mp="ofcfxn8ocwpr">
            {contact.paymentStatus}
          </Badge>;
      case 'tags':
        return <div className="flex flex-wrap gap-1" data-id="element-8236" data-mp="jhauivns0lb4">
            {contact.tags.map(tag => {
            const tagColor = tagColors.get(tag) || 'blue';
            // Check if it's a custom hex color
            const isCustomColor = tagColor.startsWith('#');
            if (isCustomColor) {
              // Convert hex to RGB for background with opacity
              const hexToRgb = (hex: string) => {
                const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                return result ? {
                  r: parseInt(result[1], 16),
                  g: parseInt(result[2], 16),
                  b: parseInt(result[3], 16)
                } : null;
              };
              const rgb = hexToRgb(tagColor);
              if (rgb) {
                return <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border" style={{
                  backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
                  color: tagColor,
                  borderColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`
                }} data-id="element-8237" data-mp="3dx20yag1k7nh">
                      {tag}
                    </span>;
              }
            }
            // Use predefined color classes for standard colors
            const colorClasses = {
              blue: 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]',
              green: 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]',
              orange: 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]',
              purple: 'bg-[#E9D5FF] text-[#6B21A8] border-[#D8B4FE]',
              gray: 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]'
            };
            return <span key={tag} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colorClasses[tagColor as keyof typeof colorClasses] || colorClasses.blue}`} data-id="element-8238" data-mp="rwgz6rpxygwx">
                  {tag}
                </span>;
          })}
          </div>;
      case 'source':
        return <span className="text-sm text-[#475569]" data-id="element-8239" data-mp="7w70jx85ga2w">{contact.source}</span>;
      case 'memberSince':
        return <span className="text-sm text-[#475569]" data-id="element-8240" data-mp="mld4tzlugqhn">{contact.memberSince}</span>;
      case 'nextBilling':
        return <span className="text-sm text-[#475569]" data-id="element-8241" data-mp="hstkmjsnix3v">{contact.nextBilling}</span>;
      case 'ltv':
        return <span className="text-sm text-[#0F172A] font-medium" data-id="element-8242" data-mp="vjf1y42giyzn">
            ${contact.ltv.toFixed(2)}
          </span>;
      case 'totalOrders':
        return <span className="text-sm text-[#475569]" data-id="element-8243" data-mp="15iw5aeu8l0sg">{contact.totalOrders}</span>;
      case 'lastOrder':
        return <span className="text-sm text-[#475569]" data-id="element-8244" data-mp="10jjmq6aeefvo">{contact.lastOrder}</span>;
      case 'aov':
        return <span className="text-sm text-[#475569]" data-id="element-8245" data-mp="37a0xbc0303db">
            ${contact.aov.toFixed(2)}
          </span>;
      default:
        return null;
    }
  };
  // Column definitions for toggle
  const allColumns = [{
    id: 'source',
    label: 'Source'
  }, {
    id: 'memberSince',
    label: 'Member Since'
  }, {
    id: 'nextBilling',
    label: 'Next Billing'
  }, {
    id: 'ltv',
    label: 'LTV'
  }, {
    id: 'totalOrders',
    label: 'Total Orders'
  }, {
    id: 'lastOrder',
    label: 'Last Order'
  }, {
    id: 'aov',
    label: 'AOV'
  }];
  // Available tags for bulk tagging
  const availableBulkTags: BulkTagModalTag[] = [{
    id: 'vip',
    label: 'VIP',
    color: 'purple',
    appliedToCount: 0
  }, {
    id: 'red-wine',
    label: 'Red Wine',
    color: 'blue',
    appliedToCount: 0
  }, {
    id: 'white-wine',
    label: 'White Wine',
    color: 'blue',
    appliedToCount: 0
  }, {
    id: 'retry',
    label: 'Retry',
    color: 'orange',
    appliedToCount: 0
  }, {
    id: 'churned',
    label: 'Churned',
    color: 'gray',
    appliedToCount: 0
  }, {
    id: 'referral',
    label: 'Referral',
    color: 'green',
    appliedToCount: 0
  }].map(tag => {
    // Calculate how many selected contacts have this tag - use 'contacts' state instead of 'mockContacts'
    const appliedCount = Array.from(selectedContacts).filter(contactId => {
      const contact = contacts.find(c => c.id === contactId);
      return contact?.tags.includes(tag.label);
    }).length;
    return {
      ...tag,
      appliedToCount: appliedCount
    };
  });
  // Handle bulk tag apply
  const handleBulkTagApply = (tagsToAdd: string[], tagsToRemove: string[], newTagsWithColors?: Map<string, {
    label: string;
    color: string;
  }>) => {
    console.log('=== BULK TAG APPLY DEBUG ===');
    console.log('Applying tags to', selectedContacts.size, 'contacts');
    console.log('Selected contact IDs:', Array.from(selectedContacts));
    console.log('Tags to add:', tagsToAdd);
    console.log('Tags to remove:', tagsToRemove);
    console.log('New tags with colors:', newTagsWithColors);
    // Update tag colors map with newly created tags
    if (newTagsWithColors && newTagsWithColors.size > 0) {
      setTagColors(prevColors => {
        const newColors = new Map(prevColors);
        newTagsWithColors.forEach((tagData, tagId) => {
          console.log(`Adding color mapping: ${tagData.label} -> ${tagData.color}`);
          newColors.set(tagData.label, tagData.color);
        });
        return newColors;
      });
    }
    // Update contacts with new tags
    setContacts(prevContacts => {
      const updatedContacts = prevContacts.map(contact => {
        if (!selectedContacts.has(contact.id)) {
          return contact;
        }
        console.log(`Processing contact ${contact.id} (${contact.name})`);
        console.log('  Current tags:', contact.tags);
        // Start with existing tags
        let updatedTags = [...contact.tags];
        // Remove tags
        updatedTags = updatedTags.filter(tag => {
          const tagId = tag.toLowerCase().replace(/\s+/g, '-');
          return !tagsToRemove.includes(tagId);
        });
        // Add new tags
        tagsToAdd.forEach(tagId => {
          // Check if it's a newly created tag
          const newTag = newTagsWithColors?.get(tagId);
          const tagLabel = newTag?.label || availableBulkTags.find(t => t.id === tagId)?.label || tagId.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
          console.log(`  Adding tag: ${tagId} -> ${tagLabel}`);
          // Only add if not already present
          if (!updatedTags.includes(tagLabel)) {
            updatedTags.push(tagLabel);
          }
        });
        console.log('  Updated tags:', updatedTags);
        return {
          ...contact,
          tags: updatedTags
        };
      });
      console.log('=== CONTACTS UPDATED ===');
      return updatedContacts;
    });
    // Close modal and clear selection
    setIsBulkTagModalOpen(false);
    setSelectedContacts(new Set());
  };
  return <div className="bg-[#F1F5F9] min-h-full p-4 md:p-6" data-id="element-8246" data-mp="ggaoeptcxa6d">
      <div className="mb-4 md:mb-6" data-id="element-8247" data-mp="gfhni5h2xew5">
        <NavBar tabs={[]} title="Contacts" data-id="element-8248" data-mp="2s3e3xsqivoty" />
      </div>
      <>
        {/* Filter Bar */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg mb-4 md:mb-6 p-4 md:p-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-8249" data-mp="2wu2wlbdxuruo">
          <div className="flex flex-col gap-4" data-id="element-8250" data-mp="2j3hfns81orzf">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-8251" data-mp="2j0lhc2sac1ag">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-id="element-8252" data-mp="2nzomdn89m7if">
                <div className="w-full lg:w-80 flex-shrink-0" data-id="element-8253" data-mp="3brvay07r1a7a">
                  <ContactSearchInput value={searchQuery} onChange={setSearchQuery} searchBy={searchBy} onSearchByChange={setSearchBy} data-id="element-8254" data-mp="3ft8j4gybdphd" />
                </div>
                <SavedViewsDropdown currentView={views.currentView} onViewSelect={views.selectView} onSaveView={(name, isShared) => views.saveView(name, isShared, filterValues, sortConfig ?? undefined)} onDeleteView={views.deleteView} onDuplicateView={views.duplicateView} onRenameView={views.renameView} hasActiveFilters={addedFilters.size > 0} savedViews={views.savedViews} className="flex-shrink-0" data-id="element-8255" data-mp="31vjxh6wfaeb9" />
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-shrink-0 w-full sm:w-auto sm:ml-auto" data-id="element-8256" data-mp="28ssk8w4i1nh1">
                {/* Quick Filters */}

                <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-8257" data-mp="2dqauqqy60ar5">
                  {/* Export Button */}
                  <ExportButton variant="contacts" onExport={async type => {
                  console.log(`Exporting ${type}`);
                }} data-id="element-8258" data-mp="1l3hmzomcyb7n" />

                  <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-8259" data-mp="1q32oo6swwxo2" />

                  <ColumnToggle columns={columns} onChange={setColumns} data-id="element-8260" data-mp="12di6vr8hw6gd" />
                </div>
              </div>
            </div>

            {addedFilters.size > 0 && <div className="flex items-center gap-3" data-id="element-8261" data-mp="179yqq98nvynn">
                <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-8262" data-mp="3klbc8lmt46jf" />
                <button onClick={clearAllFilters} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors whitespace-nowrap" data-id="element-8263" data-mp="36mk2tciw391m">
                  Clear all
                </button>
              </div>}
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedContacts.size > 0 && <ContactBulkActionsBar count={selectedContacts.size} onCreateTask={() => setIsBulkTaskModalOpen(true)} onAddTag={() => setIsBulkTagModalOpen(true)} onExportCallList={() => downloadCallList(sortedAndFilteredContacts.filter(contact => selectedContacts.has(contact.id)))} onClear={() => setSelectedContacts(new Set())} data-id="element-8264" data-mp="2suneljkcdjai" />}

        {bulkMessage && <div role="status" className="flex items-center gap-2 bg-[#F0FDF4] border border-[#BBF7D0] text-[#166534] rounded-lg px-4 py-3 mb-4 md:mb-6 text-[14px]" data-id="element-8265" data-mp="2dyo82dcvrr7h">
            <CheckCircle className="w-4 h-4 flex-shrink-0" data-id="element-8266" data-mp="299n3p2k5cnjs" />
            {bulkMessage}
          </div>}

        {/* Data Table */}
        <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-8267" data-mp="1vb6osoc4rqqk">
          <div className="overflow-x-auto" data-id="element-8268" data-mp="1zd0lymn4aclz">
            <table className="w-full" key={contacts.length} data-id="element-8269" data-mp="2n0i924vfsmim">
              <thead className="bg-[#F1F5F9]" data-id="element-8270" data-mp="2rzv3pbsjuxvr">
                <tr data-id="element-8271" data-mp="2ryjp0gxkg5pv">
                  <th className="px-3 md:px-4 py-3 w-12" data-id="element-8272" data-mp="2rz365vfe6q2u">
                    <div className="flex items-center justify-center" data-id="element-8273" data-mp="2e99hpe7su7tn">
                      <input type="checkbox" className="w-[18px] h-[18px] rounded border-2 border-[#CBD5E1] text-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-0 cursor-pointer hover:border-[#0EA5E9] transition-colors" checked={selectedContacts.size === sortedAndFilteredContacts.length && sortedAndFilteredContacts.length > 0} onChange={handleSelectAll} data-id="element-8274" data-mp="2ia1s4aseqiri" />
                    </div>
                  </th>
                  {visibleColumns.map(column => <th key={column.id} className={`px-3 md:px-4 py-3 text-left text-[12px] font-semibold uppercase tracking-[0.5px] ${column.id === 'contact' ? 'text-[#475569]' : 'text-[#64748B]'}`} data-id="element-8275" data-mp="ubix7gve3epg">
                      <button onClick={() => handleSort(column.id)} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-8276" data-mp="142lxigghvpno">
                        {column.label}
                        {sortConfig?.key === column.id ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8277" data-mp="qafhqvh97kor" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-8278" data-mp="qca2a8om5asb" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-8279" data-mp="lbvwkxahicxg" />}
                      </button>
                    </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]" data-id="element-8280" data-mp="21rp17i0ducq2">
                {sortedAndFilteredContacts.map(contact => <tr key={contact.id} className={`hover:bg-[#F8FAFC] transition-colors cursor-pointer ${selectedContacts.has(contact.id) ? 'bg-[#F0F9FF]' : ''}`} onClick={() => navigate(`/contacts/${contact.id}`)} data-id="element-8281" data-mp="1wt41djp1lv25">
                    <td className="px-3 md:px-4 py-3 md:py-4" onClick={e => e.stopPropagation()} data-id="element-8282" data-mp="falr6vnv6pc9">
                      <div className="flex items-center justify-center" data-id="element-8283" data-mp="k96vp7a5fv6i">
                        <input type="checkbox" className="w-[18px] h-[18px] rounded border-2 border-[#CBD5E1] text-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-0 cursor-pointer hover:border-[#0EA5E9] transition-colors" checked={selectedContacts.has(contact.id)} onChange={() => handleSelectContact(contact.id)} data-id="element-8284" data-mp="k72fpqyz75pv" />
                      </div>
                    </td>
                    {visibleColumns.map(column => <td key={column.id} className={`px-3 md:px-4 py-3 md:py-4 ${column.id === 'address' ? 'min-w-[180px]' : column.id === 'phone' ? 'min-w-[140px]' : column.id === 'tags' ? 'min-w-[200px]' : ''}`} data-id="element-8285" data-mp="17z167i6zx7nl">
                        {renderCell(contact, column.id)}
                      </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-4 md:mt-6" data-id="element-8286" data-mp="1d3bmm97yrxyr">
          <div className="text-[13px] text-[#64748B]" data-id="element-8287" data-mp="192rph5ne4ucm">
            Showing 1-{sortedAndFilteredContacts.length} of{' '}
            {sortedAndFilteredContacts.length} contacts
          </div>
          <div className="flex items-center gap-2" data-id="element-8288" data-mp="20tpk4f9bip0q">
            <button disabled className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out" data-id="element-8289" data-mp="20u7mzuqhk1hm">
              Previous
            </button>
            <div className="flex items-center gap-1" data-id="element-8290" data-mp="2ojtm6i6uygpm">
              <button className="w-10 h-10 rounded-md text-[13px] font-medium transition-colors duration-200 ease-in-out bg-[#0EA5E9] text-white" data-id="element-8291" data-mp="2jsbgxhxsxtyu">
                1
              </button>
            </div>
            <button disabled className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out" data-id="element-8292" data-mp="mtkyjfbgn47c">
              Next
            </button>
          </div>
        </div>

        {/* Bulk Tag Modal */}
        <BulkTagModal isOpen={isBulkTagModalOpen} onClose={() => setIsBulkTagModalOpen(false)} selectedCount={selectedContacts.size} availableTags={availableBulkTags} onApply={handleBulkTagApply} data-id="element-8293" data-mp="3v8uxiek1ztaw" />

        {/* Bulk task: one task per selected contact */}
        <CreateTaskModal isOpen={isBulkTaskModalOpen} onClose={() => setIsBulkTaskModalOpen(false)} initialLinkedTo={{
        id: 'bulk',
        type: 'contact',
        label: `${selectedContacts.size} selected ${selectedContacts.size === 1 ? 'contact' : 'contacts'} (one task each)`
      }} onCreate={taskData => {
        const targets = contacts.filter(contact => selectedContacts.has(contact.id));
        targets.forEach(contact => addTask({
          title: taskData.title,
          note: taskData.description || undefined,
          priority: taskData.priority,
          status: 'Open',
          dueDate: taskData.dueDate,
          createdBy: 'Current User',
          linkedTo: {
            id: contact.id,
            type: 'contact',
            label: contact.name
          },
          assignee: {
            name: taskData.assignee,
            avatar: taskData.assignee.split(' ').map(part => part[0]).join(''),
            email: `${taskData.assignee.toLowerCase().replace(/\s+/g, '.')}@acmewine.com`
          }
        }));
        setBulkMessage(`Created ${targets.length} ${targets.length === 1 ? 'task' : 'tasks'} assigned to ${taskData.assignee}.`);
        setIsBulkTaskModalOpen(false);
        setSelectedContacts(new Set());
      }} data-id="element-8294" data-mp="3qakyttz4eakj" />
      </>
    </div>;
}